import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  User as UserIcon,
  Building2,
  MapPin,
  Phone,
  CreditCard,
  Landmark,
  Percent,
  History,
  Settings,
  FileSpreadsheet,
  Lock,
  Search as SearchIcon,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import VoiceTextarea from "../components/VoiceTextarea";
import ContactsBlock from "../components/customer-blocks/ContactsBlock";
import CustomerLogo from "../components/customer-blocks/CustomerLogo";

/**
 * Wave 29 Chunk 10e — Full-fidelity Customer Editor (tabbed).
 * Mirrors the Pastel/Sage debtors-enquiries schema. Single full-page form,
 * grouped into 10 tabs so the screen never feels cluttered. Sensitive
 * tabs (Financial, Tax, Bank, Aging, Accounting raw) are admin/owner-only.
 */

const TEXT = "text";
const NUM = "number";
const BOOL = "bool";
const TXT_AREA = "textarea";
const DATE = "date";

const SENSITIVE_TABS = new Set(["financial", "tax", "bank", "aging", "accounting"]);

const TABS = [
  {
    key: "overview",
    label: "Overview",
    icon: UserIcon,
    color: "#1FB6C1",
    fields: [
      { k: "name", label: "Customer / company name", type: TEXT, required: true, full: true },
      { k: "customer_type", label: "Type", type: "select",
        options: [{ v: "individual", t: "Individual" }, { v: "company", t: "Company" }] },
      { k: "status", label: "Status", type: "select",
        options: [
          { v: "active", t: "Active" },
          { v: "inactive", t: "Inactive" },
          { v: "lead", t: "Lead" },
          { v: "closed", t: "Closed" },
          { v: "lost", t: "Lost" },
        ] },
      { k: "external_ref", label: "Customer ID (auto)", type: TEXT, readOnly: true },
      { k: "industry", label: "Industry", type: TEXT },
      { k: "contact_name", label: "Primary contact", type: TEXT },
      { k: "contact_position", label: "Contact position", type: TEXT },
      { k: "title", label: "Contact title (Mr/Mrs/…)", type: TEXT },
      { k: "initials", label: "Initials", type: TEXT },
      { k: "contact_gender", label: "Contact gender", type: TEXT },
      { k: "language", label: "Preferred language", type: TEXT },
      { k: "notes", label: "Notes (visible to internal staff)", type: TXT_AREA, full: true },
      { k: "special_requests", label: "Special requests (shown on Job Cards)", type: TXT_AREA, full: true },
    ],
  },
  {
    key: "contacts",
    label: "Contacts",
    icon: Phone,
    color: "#a78bfa",
    fields: [
      { k: "phone", label: "Phone (primary)", type: TEXT },
      { k: "telephone2", label: "Telephone 2", type: TEXT },
      { k: "whatsapp", label: "WhatsApp (international format)", type: TEXT },
      { k: "fax", label: "Fax", type: TEXT },
      { k: "email", label: "Email (primary)", type: TEXT },
      { k: "order_email", label: "Order processing email", type: TEXT },
      { k: "website", label: "Website", type: TEXT, full: true },
    ],
  },
  {
    key: "addresses",
    label: "Addresses",
    icon: MapPin,
    color: "#34d399",
    fields: [
      { k: "physical_address", label: "Physical address (single-line summary)", type: TEXT, full: true },
      { k: "address_line_1", label: "Physical address line 1", type: TEXT, full: true },
      { k: "address_line_2", label: "Physical address line 2", type: TEXT, full: true },
      { k: "address_line_3", label: "Physical address line 3", type: TEXT },
      { k: "address_line_4", label: "Physical address line 4", type: TEXT },
      { k: "postcode", label: "Postcode (physical)", type: TEXT },
      { k: "country", label: "Country", type: TEXT },
      { k: "delivery_address_1", label: "Delivery line 1", type: TEXT, full: true },
      { k: "delivery_address_2", label: "Delivery line 2", type: TEXT, full: true },
      { k: "delivery_address_3", label: "Delivery line 3", type: TEXT },
      { k: "delivery_address_4", label: "Delivery line 4", type: TEXT },
      { k: "delivery_postcode", label: "Delivery postcode", type: TEXT },
      { k: "billing_address", label: "Billing address", type: TEXT, full: true },
      { k: "billing_city", label: "Billing city", type: TEXT },
      { k: "billing_postal_code", label: "Billing postal code", type: TEXT },
      { k: "billing_country", label: "Billing country", type: TEXT },
      { k: "province_area", label: "Province / region", type: TEXT },
      { k: "area", label: "Area (sales / route)", type: TEXT },
      { k: "estate_reserve", label: "Estate / reserve", type: TEXT },
      { k: "branch", label: "Branch", type: TEXT },
      { k: "route", label: "Route", type: TEXT },
    ],
  },
  {
    key: "identity",
    label: "Identity",
    icon: Building2,
    color: "#fbbf24",
    fields: [
      { k: "alternative_name", label: "Alternative name", type: TEXT, full: true },
      { k: "trading_as", label: "Trading as", type: TEXT, full: true },
      { k: "registration_number", label: "Company registration #", type: TEXT },
      { k: "id_number", label: "ID number", type: TEXT },
      { k: "vat_number", label: "VAT / Tax number", type: TEXT },
      { k: "account_class", label: "Account class", type: TEXT },
      { k: "account_group", label: "Account group", type: TEXT },
      { k: "account_subgroup", label: "Account sub-group", type: TEXT },
      { k: "link_account", label: "Linked account", type: TEXT },
      { k: "star_rating", label: "Star rating (hospitality)", type: NUM },
      { k: "price_per_night", label: "Price-per-night bracket", type: TEXT },
      { k: "has_wtp", label: "Has water-treatment plant", type: BOOL },
      { k: "has_sla", label: "Existing SLA", type: BOOL },
      { k: "needs_system", label: "Needs system (confirmed)", type: BOOL },
    ],
  },
  {
    key: "financial",
    label: "Financial",
    icon: CreditCard,
    color: "#f97316",
    fields: [
      { k: "payment_terms", label: "Payment terms (code/days)", type: TEXT },
      { k: "payment_terms_custom", label: "Payment terms (custom note)", type: TEXT },
      { k: "currency", label: "Currency code", type: TEXT },
      { k: "credit_limit", label: "Credit limit (ZAR)", type: NUM },
      { k: "credit_balance", label: "Current balance (ZAR)", type: NUM },
      { k: "credit_limit_insured", label: "Insured credit limit", type: NUM },
      { k: "credit_limit_reserved", label: "Reserved credit limit", type: NUM },
      { k: "enforce_credit_limit_check", label: "Enforce credit-limit check", type: BOOL },
      { k: "cash_only", label: "Cash only", type: BOOL },
      { k: "allow_cash_sale", label: "Allow cash sale", type: BOOL },
      { k: "price_list_no", label: "Price list #", type: NUM },
      { k: "discount_matrix", label: "Discount matrix", type: TEXT },
      { k: "invoice_discount", label: "Invoice discount %", type: NUM },
      { k: "settlement_discount", label: "Settlement discount %", type: NUM },
      { k: "bulk_line_discount", label: "Bulk line discount", type: BOOL },
      { k: "charge_interest", label: "Charge interest", type: BOOL },
      { k: "interest_rate", label: "Interest rate %", type: NUM },
      { k: "min_order_value", label: "Minimum order value (ZAR)", type: NUM },
      { k: "sales_rep_code", label: "Sales rep code (NORMALREP)", type: TEXT },
      { k: "credit_check_date", label: "Last credit check date", type: DATE },
      { k: "credit_check_result", label: "Credit-check result", type: TEXT },
      { k: "credit_notes", label: "Credit notes", type: TXT_AREA, full: true },
    ],
  },
  {
    key: "tax",
    label: "Tax",
    icon: Percent,
    color: "#22d3ee",
    fields: [
      { k: "vat_number", label: "VAT number", type: TEXT },
      { k: "vat_status_code", label: "VAT status code", type: TEXT },
      { k: "preferred_tax_rate", label: "Preferred tax rate %", type: NUM },
      { k: "preferred_tax_rate_enabled", label: "Use preferred tax rate", type: BOOL },
      { k: "withholding_non_resident", label: "Withholding (non-resident)", type: BOOL },
    ],
  },
  {
    key: "bank",
    label: "Bank",
    icon: Landmark,
    color: "#60a5fa",
    fields: [
      { k: "bank_name", label: "Bank name", type: TEXT, full: true },
      { k: "bank_branch_code", label: "Branch code", type: TEXT },
      { k: "bank_branch_eft", label: "EFT branch code", type: TEXT },
      { k: "bank_account_no", label: "Account number", type: TEXT },
      { k: "bank_subaccount", label: "Sub-account / SWIFT", type: TEXT },
      { k: "bank_acc_type", label: "Account type", type: TEXT },
      { k: "bank_proof", label: "Proof reference", type: TEXT, full: true },
    ],
  },
  {
    key: "aging",
    label: "Aging & History",
    icon: History,
    color: "#f43f5e",
    fields: [
      { k: "balance_current", label: "Current", type: NUM, readOnly: true },
      { k: "balance_30", label: "30 days", type: NUM, readOnly: true },
      { k: "balance_60", label: "60 days", type: NUM, readOnly: true },
      { k: "balance_90", label: "90 days", type: NUM, readOnly: true },
      { k: "balance_120", label: "120 days", type: NUM, readOnly: true },
      { k: "balance_150", label: "150 days", type: NUM, readOnly: true },
      { k: "balance_180", label: "180+ days", type: NUM, readOnly: true },
      { k: "balance_bfwd", label: "Brought forward", type: NUM, readOnly: true },
      { k: "overdue_amount", label: "Overdue total", type: NUM, readOnly: true },
      { k: "last_payment_date", label: "Last payment date", type: DATE, readOnly: true },
      { k: "last_payment_amount", label: "Last payment amount", type: NUM, readOnly: true },
      { k: "last_debit_date", label: "Last debit date", type: DATE, readOnly: true },
      { k: "last_debit_amount", label: "Last debit amount", type: NUM, readOnly: true },
      { k: "date_opened", label: "Account opened", type: DATE, readOnly: true },
      { k: "accounting_created_at", label: "Accounting created", type: DATE, readOnly: true },
      { k: "accounting_modified_at", label: "Accounting modified", type: DATE, readOnly: true },
    ],
  },
  {
    key: "ops",
    label: "Operations",
    icon: Settings,
    color: "#84cc16",
    fields: [
      { k: "is_active_in_accounting", label: "Active in accounting", type: BOOL },
      { k: "on_hold", label: "On hold", type: BOOL },
      { k: "on_hold_type", label: "On-hold reason", type: TEXT },
      { k: "on_hold_dormant", label: "Dormant", type: BOOL },
      { k: "lead_time", label: "Lead time (days)", type: NUM },
      { k: "delivery_cycle", label: "Delivery cycle", type: TEXT },
      { k: "auto_orders_enabled", label: "Auto-orders enabled", type: BOOL },
      { k: "auto_order_frequency", label: "Auto-order frequency", type: NUM },
      { k: "auto_order_days", label: "Auto-order days", type: NUM },
      { k: "sms_marketing_opt_in", label: "SMS marketing opt-in", type: BOOL },
      { k: "loyalty_account", label: "Loyalty account", type: BOOL },
      { k: "portal_access_blocked", label: "Block portal access", type: BOOL },
      { k: "new_orders_blocked", label: "Block new orders", type: BOOL },
      { k: "block_reason", label: "Block reason", type: TEXT, full: true },
      { k: "internal_notes", label: "Internal notes (admin-only)", type: TXT_AREA, full: true },
    ],
  },
  {
    key: "accounting",
    label: "Accounting raw",
    icon: FileSpreadsheet,
    color: "#94a3b8",
    isRaw: true,
  },
];

export default function CustomerEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const isPrivileged = role === "owner" || role === "admin";

  const visibleTabs = useMemo(
    () => TABS.filter((t) => !SENSITIVE_TABS.has(t.key) || isPrivileged),
    [isPrivileged]
  );

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.key || "overview");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rawFilter, setRawFilter] = useState("");
  // Iter 418.v (Dirk, 2026-02-28) — the "All fields" editor only
  // exposed the *primary* contact fields on the customer doc, but
  // customers can have any number of role-based contacts stored in
  // the separate `contacts` collection (Billing, Site, Tech, etc.).
  // Load them so we can render the ContactsBlock inside the Contacts
  // tab — full parity with the Overview page.
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/contacts?customer_id=${id}`);
        if (live) setContacts(data || []);
      } catch {
        if (live) setContacts([]);
      }
    })();
    return () => { live = false; };
  }, [id]);

  const reloadContacts = async () => {
    try {
      const { data } = await api.get(`/contacts?customer_id=${id}`);
      setContacts(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/customers/${id}`);
        if (live) setForm(data);
      } catch (e) {
        if (live) setErr(formatErr(e.response?.data?.detail) || "Could not load customer.");
      }
    })();
    return () => { live = false; };
  }, [id]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form) return;
    setBusy(true);
    setErr("");
    try {
      const payload = { ...form };
      // The backend regenerates external_ref if missing — never send the readonly one back as a write request.
      delete payload._id;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      const { data } = await api.put(`/customers/${id}`, payload);
      setForm(data);
      toast.success("Customer saved");
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || "Save failed.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return <div className="text-[#647087] p-6" data-testid="customer-editor-loading">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="customer-editor">
      <Link
        to={`/customers/${id}`}
        className="inline-flex items-center gap-2 text-xs text-[#647087] hover:text-white"
        data-testid="customer-editor-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to customer
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">
            Customer · edit · {form.external_ref || form.id?.slice(0, 8)}
          </div>
          <div className="flex items-center gap-3 min-w-0">
            <CustomerLogo customer={form} canEdit onChanged={async () => { const { data } = await api.get(`/customers/${id}`); setForm((p) => ({ ...p, logo_upload_id: data.logo_upload_id })); }} />
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight truncate">{form.name}</h1>
          </div>
          {!isPrivileged && (
            <p className="text-[11px] text-[#647087] mt-1 flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Financial, Tax, Bank, Aging &amp; raw-accounting data is restricted to admin/owner.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/customers/${id}`)}
            className="hv-btn hv-btn-ghost"
            data-testid="customer-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="hv-btn hv-btn-primary"
            data-testid="customer-editor-save"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-rose-300 border border-rose-500/30 rounded-md p-3 bg-rose-500/5" data-testid="customer-editor-error">
          {err}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList
          className="w-full flex flex-wrap gap-0 h-auto bg-transparent border-b border-white/10 p-0 rounded-none"
          data-testid="customer-editor-tabs"
        >
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={`customer-editor-tab-${t.key}`}
                className="flex-1 min-w-[110px] gap-1.5 text-xs font-semibold rounded-none border-b-2 border-transparent text-[#a0aabf] data-[state=active]:bg-white/5 data-[state=active]:text-white data-[state=active]:shadow-none hover:text-white py-2.5 transition-colors"
                style={{ "--tab-color": t.color }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: t.color }} /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {visibleTabs.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-5">
            {t.isRaw ? (
              <RawAccountingPanel
                accounting={form.accounting || {}}
                filter={rawFilter}
                setFilter={setRawFilter}
              />
            ) : (
              <div className="space-y-5">
                {/* Iter 418.v — surface ALL role-based contacts on the
                    Contacts tab so "All fields" no longer looks like it
                    only holds a single primary contact. */}
                {t.key === "contacts" && (
                  <ContactsBlock
                    customerId={id}
                    contacts={contacts}
                    canEdit
                    onChanged={reloadContacts}
                    defaultOpen
                  />
                )}
                <FieldGrid
                  fields={t.fields || []}
                  form={form}
                  set={set}
                  tabColor={t.color}
                  tabKey={t.key}
                />
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function FieldGrid({ fields, form, set, tabColor, tabKey }) {
  return (
    <div
      className="hv-card p-5 md:p-6"
      style={{ borderColor: `${tabColor}33` }}
      data-testid={`customer-editor-panel-${tabKey}`}
    >
      <div className="grid md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <FieldCell key={f.k} f={f} value={form[f.k]} onChange={(v) => set(f.k, v)} />
        ))}
      </div>
    </div>
  );
}

function FieldCell({ f, value, onChange }) {
  const wrap = f.full ? "md:col-span-2" : "";
  const tid = `customer-editor-field-${f.k}`;
  const label = (
    <label className="text-[11px] uppercase tracking-wider text-[#a0aabf] flex items-center gap-1.5">
      {f.label}
      {f.required && <span className="text-rose-300">*</span>}
      {f.readOnly && <Lock className="w-2.5 h-2.5 opacity-60" />}
    </label>
  );

  if (f.type === BOOL) {
    const v = value === true || value === "true" || value === 1;
    return (
      <div className={wrap}>
        {label}
        <button
          type="button"
          onClick={() => !f.readOnly && onChange(!v)}
          disabled={f.readOnly}
          data-testid={tid}
          className={`mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium transition ${
            v ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200" : "border-white/10 bg-white/[0.02] text-[#a0aabf]"
          } ${f.readOnly ? "opacity-60 cursor-not-allowed" : "hover:border-white/20"}`}
        >
          <span className={`inline-block w-2 h-2 rounded-full ${v ? "bg-emerald-400" : "bg-[#647087]"}`} />
          {v ? "Yes" : "No"}
        </button>
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <div className={wrap}>
        {label}
        <select
          className="hv-input mt-1"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={f.readOnly}
          data-testid={tid}
        >
          <option value="" disabled>—</option>
          {f.options.map((o) => (
            <option key={o.v} value={o.v}>{o.t}</option>
          ))}
        </select>
      </div>
    );
  }
  if (f.type === TXT_AREA) {
    return (
      <div className={wrap}>
        {label}
        <VoiceTextarea
          rows={3}
          value={value ?? ""}
          onChange={onChange}
          readOnly={f.readOnly}
          data-testid={tid}
         context="note" />
      </div>
    );
  }
  if (f.type === NUM) {
    return (
      <div className={wrap}>
        {label}
        <input
          type="number"
          step="any"
          className="hv-input mt-1"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          readOnly={f.readOnly}
          data-testid={tid}
        />
      </div>
    );
  }
  if (f.type === DATE) {
    // Accept either ISO date or ISO datetime.
    const v = value ? String(value).slice(0, 10) : "";
    return (
      <div className={wrap}>
        {label}
        <input
          type="date"
          className="hv-input mt-1"
          value={v}
          onChange={(e) => onChange(e.target.value || null)}
          readOnly={f.readOnly}
          data-testid={tid}
        />
      </div>
    );
  }
  return (
    <div className={wrap}>
      {label}
      <input
        type="text"
        className="hv-input mt-1"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={f.readOnly}
        required={f.required}
        data-testid={tid}
      />
    </div>
  );
}

function RawAccountingPanel({ accounting, filter, setFilter }) {
  const entries = useMemo(() => {
    const all = Object.entries(accounting || {}).sort(([a], [b]) => a.localeCompare(b));
    if (!filter.trim()) return all;
    const q = filter.trim().toLowerCase();
    return all.filter(([k, v]) =>
      k.toLowerCase().includes(q) || String(v ?? "").toLowerCase().includes(q)
    );
  }, [accounting, filter]);

  return (
    <div className="hv-card p-5 md:p-6 space-y-4" data-testid="customer-editor-panel-accounting">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white">Raw accounting fields</div>
          <p className="text-[11px] text-[#647087]">
            Every column the accounting system sent us that doesn't live in a curated tab. Read-only — edit upstream in your accounting system, then re-import.
          </p>
        </div>
        <div className="relative w-full md:w-64">
          <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            placeholder="Filter keys / values…"
            className="hv-input pl-7"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="customer-editor-raw-filter"
          />
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="text-sm text-[#647087] border border-dashed border-white/10 rounded-md p-6 text-center" data-testid="customer-editor-raw-empty">
          {Object.keys(accounting || {}).length === 0
            ? "No raw accounting data captured for this customer yet. Run a customer import to populate."
            : "No matches for that filter."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="customer-editor-raw-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/10">
                <th className="text-left py-2 pr-3 font-medium">Column</th>
                <th className="text-left py-2 font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([k, v]) => (
                <tr key={k} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`customer-editor-raw-row-${k}`}>
                  <td className="py-1.5 pr-3 font-mono text-[#a0aabf] whitespace-nowrap">{k}</td>
                  <td className="py-1.5 text-white break-all">{String(v ?? "")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
